import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, Zap, Trash2, RefreshCw, ExternalLink, Clock, Copy, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'uandfashion_admin_key';

const AdminActionAlertsPage = () => {
  const [keyInput, setKeyInput] = useState('');
  const [authKey, setAuthKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [showWebhookKey, setShowWebhookKey] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ brand_name: '', action_text: '', affiliate_link: '', image_url: '', valid_until: '' });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  const fetchList = useCallback(async (key) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/action-alerts/admin/list`, { params: { key } });
      setAlerts(res.data.alerts || []);
      sessionStorage.setItem(STORAGE_KEY, key);
      setAuthKey(key);
    } catch (err) {
      setError(
        err?.response?.status === 403
          ? 'Onjuist wachtwoord — probeer opnieuw.'
          : 'Kan de lijst niet ophalen.'
      );
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthKey('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authKey) fetchList(authKey);
  }, [authKey, fetchList]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (keyInput.trim()) fetchList(keyInput.trim());
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthKey('');
    setKeyInput('');
    setAlerts([]);
  };

  const handleDelete = async (alert) => {
    if (!window.confirm(`Verwijder actie "${alert.action_text}" van ${alert.brand_name}?`)) return;
    setDeleting(alert.id);
    try {
      await axios.delete(`${API}/action-alerts/${alert.id}`, { params: { key: authKey } });
      setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
    } catch {
      alert('Verwijderen mislukt.');
    } finally {
      setDeleting(null);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    const body = {
      brand_name: addForm.brand_name.trim(),
      action_text: addForm.action_text.trim(),
      affiliate_link: addForm.affiliate_link.trim(),
      image_url: addForm.image_url.trim() || null,
      valid_until: addForm.valid_until.trim() || null,
    };
    if (!body.brand_name || !body.action_text || !body.affiliate_link) {
      setAddError('Merknaam, actietekst en affiliate-link zijn verplicht.');
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/action-alerts`, body, { params: { key: authKey } });
      setAddForm({ brand_name: '', action_text: '', affiliate_link: '', image_url: '', valid_until: '' });
      setShowAddForm(false);
      await fetchList(authKey);
    } catch (err) {
      setAddError(err?.response?.data?.detail || 'Opslaan mislukt. Controleer de velden.');
    } finally {
      setAdding(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  if (!authKey) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="action-alerts-admin-login">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 p-3 rounded-full mb-4">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Beheer — Action Alerts</h1>
            <p className="text-sm text-gray-500 text-center">
              Voer je beheerderswachtwoord in om alle acties te bekijken en webhook-instellingen op te halen.
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Wachtwoord"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="action-alerts-admin-key-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !keyInput.trim()}
              className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-60"
              data-testid="action-alerts-admin-login-button"
            >
              {loading ? 'Bezig...' : 'Inloggen'}
            </button>
            {error && <p className="text-sm text-red-600 text-center">{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="action-alerts-admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Action Alerts</h1>
            <p className="text-sm text-gray-500 mt-1">
              Beheer realtime partner-acties die via Make.com (of een ander tool) worden gepusht.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchList(authKey)}
              className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Vernieuwen
            </button>
            <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-gray-700 px-3">
              Uitloggen
            </button>
          </div>
        </div>

        {/* Add new action — manual entry */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Nieuwe actie toevoegen</h2>
            <button
              onClick={() => setShowAddForm((v) => !v)}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:shadow-md transition-shadow"
              data-testid="action-alerts-toggle-add-form"
            >
              <Plus className="w-4 h-4" />
              {showAddForm ? 'Sluiten' : 'Voeg actie toe'}
            </button>
          </div>
          {showAddForm && (
            <form onSubmit={handleAdd} className="space-y-4" data-testid="action-alerts-add-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Merknaam <span className="text-pink-500">*</span></label>
                  <input
                    type="text"
                    value={addForm.brand_name}
                    onChange={(e) => setAddForm((f) => ({ ...f, brand_name: e.target.value }))}
                    placeholder="bv. Superdry"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="add-form-brand"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Geldig tot (optioneel)</label>
                  <input
                    type="date"
                    value={addForm.valid_until}
                    onChange={(e) => setAddForm((f) => ({ ...f, valid_until: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="add-form-valid-until"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Actietekst <span className="text-pink-500">*</span></label>
                <input
                  type="text"
                  value={addForm.action_text}
                  onChange={(e) => setAddForm((f) => ({ ...f, action_text: e.target.value }))}
                  placeholder="bv. Mid-season sale -30% op alle jurken"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="add-form-text"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Affiliate link <span className="text-pink-500">*</span></label>
                <input
                  type="url"
                  value={addForm.affiliate_link}
                  onChange={(e) => setAddForm((f) => ({ ...f, affiliate_link: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="add-form-link"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Banner / afbeelding URL <span className="text-gray-400 font-normal">(optioneel — toont grote banner i.p.v. iconen)</span>
                </label>
                <input
                  type="url"
                  value={addForm.image_url}
                  onChange={(e) => setAddForm((f) => ({ ...f, image_url: e.target.value }))}
                  placeholder="https://... (banner van het merk of affiliate-netwerk)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="add-form-image-url"
                />
              </div>
              {addError && <p className="text-sm text-red-600">{addError}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddForm(false); setAddError(''); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Annuleer
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  className="bg-gray-900 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
                  data-testid="add-form-submit"
                >
                  {adding ? 'Opslaan…' : 'Actie toevoegen'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Webhook info */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-purple-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Webhook configuratie (voor Make.com)</h2>
          <div className="space-y-2 text-sm">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-gray-500 font-medium sm:w-32">Endpoint URL</span>
              <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-gray-800 text-xs break-all">
                {process.env.REACT_APP_BACKEND_URL}/api/action-alerts
              </code>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-gray-500 font-medium sm:w-32">Method</span>
              <code className="px-3 py-2 bg-gray-50 rounded-lg text-gray-800 text-xs">POST</code>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-gray-500 font-medium sm:w-32">Header</span>
              <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-gray-800 text-xs">
                X-API-Key: <button
                  onClick={() => setShowWebhookKey((v) => !v)}
                  className="text-purple-600 underline ml-1"
                  data-testid="toggle-webhook-key"
                >
                  {showWebhookKey ? 'verbergen' : 'toon webhook-key'}
                </button>
                {showWebhookKey && (
                  <span className="block mt-1 text-pink-600 break-all">
                    SIb5KtXCWWx3Kc4-Qs_r3smuK8bGenQjDkSUxQycbV8
                    <button
                      onClick={() => navigator.clipboard?.writeText('SIb5KtXCWWx3Kc4-Qs_r3smuK8bGenQjDkSUxQycbV8')}
                      className="ml-2 text-gray-500 hover:text-purple-600"
                      title="Kopieer"
                    >
                      <Copy className="w-3 h-3 inline" />
                    </button>
                  </span>
                )}
              </code>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Bewaar deze key veilig — wie hem heeft kan acties op je site posten. Wijzig in <code>backend/.env</code> → <code>ACTION_ALERTS_WEBHOOK_KEY</code> om hem te roteren.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center gap-3 text-gray-500 text-sm mb-1">
            <Zap className="w-4 h-4" /> Totaal acties
          </div>
          <p className="text-3xl font-bold text-gray-900" data-testid="action-alerts-admin-stat-total">
            {alerts.length}
          </p>
        </div>

        <div className="space-y-3">
          {loading && alerts.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500">Lijst aan het laden…</div>
          ) : alerts.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500" data-testid="action-alerts-admin-empty">
              Nog geen acties. Zodra Make.com een POST stuurt naar de webhook URL, verschijnt het hier.
            </div>
          ) : (
            alerts.map((alert) => (
              <div key={alert.id} className="bg-white rounded-xl shadow-sm p-5" data-testid={`action-alerts-admin-row-${alert.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-xs uppercase tracking-wider text-pink-500 font-medium">{alert.brand_name}</span>
                      <span className="text-xs text-gray-400">· {formatDate(alert.created_at)}</span>
                      {alert.valid_until && (
                        <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" /> tot {alert.valid_until}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-900 font-medium mb-2">{alert.action_text}</p>
                    <a
                      href={alert.affiliate_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 break-all"
                    >
                      <ExternalLink className="w-3 h-3" /> {alert.affiliate_link}
                    </a>
                  </div>
                  <button
                    onClick={() => handleDelete(alert)}
                    disabled={deleting === alert.id}
                    className="text-red-600 hover:text-red-800 text-sm font-medium inline-flex items-center gap-1 disabled:opacity-50 self-start"
                  >
                    <Trash2 className="w-4 h-4" /> {deleting === alert.id ? 'Bezig…' : 'Verwijder'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminActionAlertsPage;
